import { IPaymentsCurrencyConfig, ISiteConfigs } from "@finbackoffice/clientbff-client";
import { Currency, FeeType } from "@finbackoffice/enums";
import { ICurrencyFormatConfig, DigitSeparators, CurrencySymbolsMap } from "@finbackoffice/fe-core";

export type ICurrencyConfig = Partial<{
    enabled: boolean;
    confirmation_count: number;
    digits: number;
    deposit: {
        min: number;
        max_usd: number;
        enabled: boolean;
    };
    withdrawal: {
        auto: { enabled: boolean; max_amount: number };
        min: number;
        max_usd: number;
        enabled: boolean;
    };
    fee: {
        cover_fee: boolean;
        fee_type: FeeType;
        gas_price: number;
        scale: number;
    };
}> & {
    currency: Currency;
};

export type IPaymentConfig = Partial<Record<Currency, Omit<ICurrencyConfig, "currency">>>;
export function getCurrencyConfig(siteConfig: ISiteConfigs, currency: Currency) {
    const config = (siteConfig.payments as any).currency as IPaymentConfig;
    return config[currency] ?? {};
}

export function round(value: number, digits = 2): number {
    const roundTo = Math.pow(10, digits);
    return Math.floor(value * roundTo) / roundTo;
}

export function numberWithCommas(value: string): string {
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}

export function formatCurrency(value: number | string, digits: number, withCommas = true): string {
    const numValue = typeof value !== "number" ? parseFloat(value) : value;

    if (isNaN(numValue) === true) {
        return "0";
    }
    const roundValue = round(numValue, digits).toFixed(digits);
    return withCommas ? numberWithCommas(roundValue) : roundValue;
}

type IFormatAmount = {
    format: ICurrencyFormatConfig | null;
    amount: string;
    withCode?: boolean;
    withSymbol?: boolean;
};

export function formatAmount({
    format,
    amount,
    withCode = true,
    withSymbol,
}: IFormatAmount): string {
    if (format) {
        const fixedPointAmount = parseFloat(amount).toFixed(format.decimal_digits);

        const partials: string[] = fixedPointAmount.split(".");
        let int = partials[0];
        const float =
            partials.length > 1 ? DigitSeparators[format.decimal_separator] + partials[1] : "";

        const rgx = /(\d+)(\d{3})/;

        if (DigitSeparators[format.thousands_separator]) {
            while (rgx.test(int)) {
                int = int.replace(rgx, `$1${DigitSeparators[format.thousands_separator]}$2`);
            }
        }

        const number = `${int}${float}`;

        return `${withSymbol && format.symbol ? format.symbol : ""}${
            withSymbol ? ` ` : ""
        }${number}${withCode && format.code ? ` ${format.code}` : ""}`;
    }
    return amount;
}

export const convertToCurrencyFormatterConfig = (
    currency: Currency,
    paymentConfig: IPaymentsCurrencyConfig,
): ICurrencyFormatConfig => {
    return {
        code: currency,
        decimal_digits: paymentConfig.digits ?? 0,
        decimal_separator: "dot",
        symbol: CurrencySymbolsMap[currency as keyof typeof CurrencySymbolsMap] || "",
        thousands_separator: "comma",
    };
};
